import { useEffect, useMemo, useState } from 'react';
import { AppointmentsService } from '../../api/AppointmentsAPI';
import { getPhotoIdentity } from '../../utils';
import s from '../../styles/dashboard.module.css';

const phaseOf = (p) => p.phase || 'photo';
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

// Full-screen carousel of an appointment's uploaded photos, with per-phase tabs.
export function PhotoCarouselModal({ appt, date, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx]         = useState(0);
  const [activePhase, setActivePhase] = useState(null);

  useEffect(() => {
    const { idBooking, customerName, serviceLabel } = getPhotoIdentity(appt);
    AppointmentsService.getPhotos({ idBooking, customerName, serviceLabel, date })
      .then(d => { setPhotos(d.photos || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [appt.id, appt.externalId, date]);

  // The distinct phases present, in order of first appearance.
  const phasesPresent = useMemo(() => {
    const seen = [];
    for (const p of photos) {
      const ph = phaseOf(p);
      if (!seen.includes(ph)) seen.push(ph);
    }
    return seen;
  }, [photos]);

  // Fall back to the first available phase until the user picks a tab.
  const currentPhase = phasesPresent.includes(activePhase) ? activePhase : (phasesPresent[0] ?? null);

  const filtered = useMemo(
    () => (currentPhase ? photos.filter(p => phaseOf(p) === currentPhase) : []),
    [photos, currentPhase],
  );

  // Reset to the first photo whenever the phase changes or photos reload.
  useEffect(() => { setIdx(0); }, [currentPhase, photos]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  setIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIdx(i => Math.min(filtered.length - 1, i + 1));
      if (e.key === 'Escape')     onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [filtered.length, onClose]);

  const photo = filtered[idx];

  return (
    <div className={s.carouselOverlay} onClick={onClose}>
      <div className={s.carouselPanel} onClick={e => e.stopPropagation()}>
        <div className={s.carouselHeader}>
          <div>
            <div className={s.carouselCar}>{appt.car}</div>
            <div className={s.carouselService}>{appt.service}</div>
          </div>
          <button className={s.carouselClose} onClick={onClose}>✕</button>
        </div>

        {loading && <div className={s.carouselLoading}>Loading photos…</div>}
        {!loading && photos.length === 0 && <div className={s.carouselLoading}>No photos found</div>}

        {!loading && photos.length > 0 && phasesPresent.length > 1 && (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '4px 0 12px', flexWrap: 'wrap' }}>
            {phasesPresent.map(tab => {
              const count  = photos.filter(p => phaseOf(p) === tab).length;
              const label  = capitalize(tab);
              const active = currentPhase === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActivePhase(tab)}
                  style={{
                    cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, letterSpacing: 0.5,
                    textTransform: 'uppercase', fontWeight: 600, padding: '6px 12px', borderRadius: 6,
                    border: '1px solid',
                    borderColor: active ? 'var(--accent)' : 'var(--border)',
                    background:  active ? 'var(--accent-wash)' : 'transparent',
                    color:       active ? 'var(--accent)' : 'var(--text-muted-on-light)',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {!loading && photos.length > 0 && photo && (
          <>
            <div className={s.carouselImgWrap}>
              <button className={`${s.carouselArrow} ${s.carouselArrowLeft}`}
                onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}>‹</button>
              <img src={photo.webViewLink} alt={photo.name} className={s.carouselImg} />
              <button className={`${s.carouselArrow} ${s.carouselArrowRight}`}
                onClick={() => setIdx(i => Math.min(filtered.length - 1, i + 1))} disabled={idx === filtered.length - 1}>›</button>
            </div>
            <div className={s.carouselMeta}>
              <span className={s.carouselCount}>{idx + 1} / {filtered.length}</span>
            </div>
            <div className={s.carouselDots}>
              {filtered.map((_, i) => (
                <button key={i} className={`${s.carouselDot} ${i === idx ? s.carouselDotActive : ''}`}
                  onClick={() => setIdx(i)} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
